import type MP4Box from 'mp4box';
import { TrackEventProp } from '@studio/constants/tracking-constants';
import { MB } from '../../constants';
import type { AddError, AddWarning, VideoUploadTrackEventProperties } from '../../types/upload-video';
import { VALIDATIONS } from '../../types/upload-video';
import { areDefined } from '../areDefined';

const calculateGop = ({
  mp4BoxFile,
  info,
  addError,
  addWarning,
  trackEventProperties = {},
}: {
  mp4BoxFile: MP4Box.MP4File;
  info: MP4Box.Mp4BoxInfo;
  addError: AddError;
  addWarning: AddWarning;
  trackEventProperties?: VideoUploadTrackEventProperties;
}): number | null => {
  let longestSyncInterval = 0;
  let largestMbps = 0;
  let inconsistentGop = false;

  for (const track of info.videoTracks) {
    const samples: MP4Box.TrackSample[] = mp4BoxFile.getTrackSamplesInfo(track.id);
    let totalBytes = 0;
    let lastSyncCts = undefined;

    const syncIntervals: number[] = [];

    // Find I-frames intervals and max data rate over those intervals
    for (const sample of samples) {
      const { cts, is_sync: isSync, size, timescale } = sample;

      if (!areDefined(cts, timescale, isSync)) {
        console.error(`CTS: ${cts}, TimeScale: ${timescale}, or isSync: ${isSync} is undefined`);
        addError(VALIDATIONS.MAX_GOP);
        return null;
      }

      if (isSync) {
        // First frame is always an I-frame, so delta must be zero
        const syncDelta = lastSyncCts !== undefined ? cts - lastSyncCts : 0;
        const syncDeltaSeconds = syncDelta / timescale;

        // Compute bytes for preceding I-frame interval
        const mbps = syncDeltaSeconds > 0 ? totalBytes / syncDeltaSeconds / MB : 0;
        if (mbps > largestMbps) {
          largestMbps = mbps;
        }

        lastSyncCts = cts;
        totalBytes = 0;

        // Skip tracking sync interval for first I-frame
        if (syncDelta > 0) {
          syncIntervals.push(syncDeltaSeconds);
          if (syncDeltaSeconds > longestSyncInterval) {
            longestSyncInterval = syncDeltaSeconds;
          }
        }
      }
      totalBytes += size;
    }

    console.log(`Max inter-I-frame bitrate: ${largestMbps.toFixed(3)} Mbps`);

    // Determine whether I-frames occur at constant period without negative detection on extra I-frames
    let intervalAccumulator = 0;

    for (const syncInterval of syncIntervals) {
      intervalAccumulator += syncInterval;
      const delta = round(intervalAccumulator - longestSyncInterval);

      if (delta > 0) {
        console.warn('Inconsistent GoP will force transcoding; ', syncIntervals);
        inconsistentGop = true;
        break;
      }

      if (delta === 0) {
        intervalAccumulator = 0;
      }
    }
  }

  // Warn if the GOP (in frames) is < 1/2 the framerate.
  // A GOP duration (or keyframe interval) of 1 second will contain $framerate frames.
  // Therefore, a keyframe interval of 0.5 secs will contain $framerate / 2 frames.
  if (longestSyncInterval < 0.5) {
    addWarning(VALIDATIONS.KEYFRAME_INTERVAL);
  }
  if (inconsistentGop) {
    addWarning(VALIDATIONS.KEYFRAME_INCONSISTENCY);
  } else {
    console.log(`GoP: ${longestSyncInterval}s`);
  }

  trackEventProperties[TrackEventProp.VIDEO_UPLOAD_GOP_DURATION] = longestSyncInterval;
  trackEventProperties[TrackEventProp.VIDEO_UPLOAD_KEYFRAME_MBPS] = Number.parseFloat(largestMbps.toFixed(2));

  return inconsistentGop ? null : longestSyncInterval;
};

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}

export default calculateGop;
